import { useMemo, useRef, useSyncExternalStore } from 'react';
import { ReactTableController, type ReactTableState } from '../ReactTableController';
import { shallowEqual } from '@ayocore/platform/client/utils/shallowEqual';

/**
 * Хук для подписки на состояние таблицы через useSyncExternalStore.
 * Поддерживает селекторы для оптимизации ре-рендеров.
 *
 * @example
 * // Подписка на всё состояние
 * const state = useReactTableController(tableId);
 *
 * @example
 * // Подписка только на сортировку (оптимизация)
 * const sorting = useReactTableController(tableId, s => s.sorting);
 */
export function useReactTableController(tableId: string): ReactTableState;
export function useReactTableController<S>(
  tableId: string,
  selector: (state: ReactTableState) => S
): S;
export function useReactTableController<S = ReactTableState>(
  tableId: string,
  selector?: (state: ReactTableState) => S
): S | ReactTableState {
  const controller = useMemo(() => ReactTableController.getInstance(tableId), [tableId]);

  const state = useSyncExternalStore(
    controller.subscribe,
    controller.getSnapshot,
    controller.getSnapshot
  );

  // Если селектор не передан — возвращаем всё состояние
  if (!selector) return state;

  // Оптимизация: возвращаем тот же объект, если данные не изменились
  const selectedRef = useRef<S | undefined>(undefined);

  return useMemo(() => {
    const selected = selector(state);

    // Shallow-сравнение (эффективнее чем JSON.stringify)
    if (selectedRef.current !== undefined) {
      if (shallowEqual(selectedRef.current, selected)) {
        return selectedRef.current as S;
      }
    }

    selectedRef.current = selected;
    return selected;
  }, [state, selector]);
}

/**
 * Хук для получения экземпляра контроллера (для вызова методов).
 *
 * @example
 * const controller = useReactTableControllerInstance(tableId);
 * controller.setSorting([{ id: 'name', desc: false }]);
 */
export function useReactTableControllerInstance(tableId: string): ReactTableController {
  return useMemo(() => ReactTableController.getInstance(tableId), [tableId]);
}
